import React, { useState, useEffect, useCallback } from 'react';
import {
  Upload, Zap, Clock, CheckCircle, XCircle,
  Download, Trash2, Film, Square, Plus, LogOut, Calendar, RefreshCw, ImageIcon
} from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import UploadLauncher, { UploadItem } from './components/upload/UploadLauncher';
import QuickConfigModal, { QuickUploadConfig } from './components/upload/QuickConfigModal';
import { uploadVideoFile } from './lib/uploadVideo';
import { formatUploadBytes } from './lib/uploadVideo';
import ComparisonModal from './components/comparison/ComparisonModal';
import RedoConversionModal, { RedoConfig } from './components/conversion/RedoConversionModal';
import Tooltip from './components/ui/Tooltip';
import NeonProgress from './components/ui/NeonProgress';


// Enhanced job status types for finite state machine
type JobStatus = "idle" | "uploading" | "queued" | "processing" | "completed" | "failed" | "deleting" | "cancelling";

interface Screenshot {
  id: string;
  jobId: string;
  type: 'original' | 'processed';
  timestamp: number;
  filePath: string;
  createdAt: string;
}

interface VideoJob {
  id: string;
  filename: string;
  originalSize: number;
  compressedSize?: number;
  status: JobStatus;
  progress: number; // 0..100
  createdAt: string;
  expiresAt?: string;
  /** Bytes enviados (só cards locais de upload) */
  uploadLoaded?: number;
  uploadTotal?: number;
  processingTime?: number;
  estimatedTimeRemaining?: number; // seconds
  outputUrl?: string;
  previewUrl?: string;
  error?: string;
  codec?: string;
  targetSizeMB?: number;
  screenshots?: Screenshot[];
  parentId?: string;
  children?: VideoJob[];
  // Hierarchy display properties
  isParent?: boolean;
  hasChildren?: boolean;
  isChild?: boolean;
  parentJob?: VideoJob;
}

interface RetroButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

// Retro Button Component
const RetroButton: React.FC<RetroButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  className = '',
  ...props
}) => {
  const baseClasses = 'retro-btn';
  const variantClasses = {
    primary: 'retro-btn-primary',
    secondary: 'retro-btn-secondary',
    ghost: 'retro-btn-ghost'
  };
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Retro Card Component
const RetroCard: React.FC<{ children: React.ReactNode; hover?: boolean; className?: string }> = ({
  children,
  hover = false,
  className = ''
}) => {
  return (
    <div className={`${hover ? 'retro-card-hover' : 'retro-card'} ${className}`}>
      {children}
    </div>
  );
};

// Modern Neon Progress - now using external component

// Retro Progress Component (fallback)

// Status Badge Component
const StatusBadge: React.FC<{ status: VideoJob['status'] }> = ({ status }) => {
  const statusConfig = {
    idle: { icon: Clock, className: 'retro-status-warn', text: 'Idle' },
    uploading: { icon: Upload, className: 'retro-status-processing', text: 'Enviando' },
    queued: { icon: Clock, className: 'retro-status-warn', text: 'Na Fila' },
    processing: { icon: Clock, className: 'retro-status-processing', text: 'Processando' },
    completed: { icon: CheckCircle, className: 'retro-status-ok', text: 'Concluído' },
    failed: { icon: XCircle, className: 'retro-status-error', text: 'Falhou' },
    deleting: { icon: Trash2, className: 'retro-status-error', text: 'Deletando' },
    cancelling: { icon: XCircle, className: 'retro-status-warn', text: 'Cancelando' },
    // Legacy fallbacks
    pending: { icon: Clock, className: 'retro-status-warn', text: 'Pendente' }
  };

  const config = statusConfig[status];
  const IconComponent = config.icon;

  return (
    <span className={config.className}>
      <IconComponent className="w-3 h-3" />
      {config.text}
    </span>
  );
};

function App() {
  const { user, logout } = useAuth();
  const [jobs, setJobs] = useState<VideoJob[]>([]);
  const [filterType, setFilterType] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [customDate, setCustomDate] = useState<string>('');
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    job: VideoJob | null;
  }>({ isOpen: false, job: null });
  const [showUploadLauncher, setShowUploadLauncher] = useState(false);
  const [advancedUploadProgress, setAdvancedUploadProgress] = useState<
    Record<string, { loaded: number; total: number }>
  >({});
  const [isAdvancedUploading, setIsAdvancedUploading] = useState(false);
  const [quickUploadProgress, setQuickUploadProgress] = useState<{
    currentIndex: number;
    totalFiles: number;
    loaded: number;
    total: number;
    fileName?: string;
  } | null>(null);
  const [isQuickUploading, setIsQuickUploading] = useState(false);
  const [quickConfigModal, setQuickConfigModal] = useState<{
    isOpen: boolean;
    files: File[];
  }>({ isOpen: false, files: [] });
  const [comparisonModal, setComparisonModal] = useState<{
    isOpen: boolean;
    jobId: string;
    selectedTimestamp?: number;
  }>({ isOpen: false, jobId: '', selectedTimestamp: undefined });
  const [redoConversionModal, setRedoConversionModal] = useState<{
    isOpen: boolean;
    job: VideoJob | null;
  }>({ isOpen: false, job: null });

  // Load jobs from backend with optional filter
  const loadJobs = useCallback(async (filter?: 'all' | 'today' | 'week' | 'month', customDateValue?: string) => {
    try {
      let url = '/api/jobs';

      // Apply filter based on type
      if (filter && filter !== 'all') {
        // Usar data de São Paulo (GMT-3) para filtros
        const now = new Date();
        const saoPauloDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
        const year = saoPauloDate.getFullYear();
        const month = saoPauloDate.getMonth() + 1;
        const day = saoPauloDate.getDate();

        switch (filter) {
          case 'today':
            url = `/api/jobs/day/${year}/${month}/${day}`;
            break;
          case 'week':
            // Calculate ISO week number
            const getWeek = (date: Date) => {
              const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
              d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
              const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
              const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
              return weekNo;
            };
            const week = getWeek(saoPauloDate);
            url = `/api/jobs/week/${year}/${week}`;
            break;
          case 'month':
            url = `/api/jobs/month/${year}/${month}`;
            break;
        }
      } else if (customDateValue) {
        // Handle custom date
        const date = new Date(customDateValue);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        url = `/api/jobs/day/${year}/${month}/${day}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const apiResponse = await response.json();
        const backendJobs = apiResponse.data?.jobs || apiResponse.data || [];
        // Convert backend format to frontend format
        const convertedJobs = backendJobs.map((job: any) => ({
          id: job.id,
          filename: job.originalName,
          originalSize: job.originalSize,
          compressedSize: job.outputSize,
          status: job.status === 'completed' ? 'completed' :
                 job.status === 'processing' ? 'processing' :
                 job.status === 'pending' ? 'queued' :
                 job.status === 'failed' ? 'failed' : 'idle',
          progress: job.progress || 0,
          createdAt: job.createdAt,
          expiresAt: job.expiresAt,
          processingTime: job.processingTime,
          estimatedTimeRemaining: job.estimatedTimeRemaining,
          outputUrl: job.outputPath,
          previewUrl: job.previewPath,
          error: job.errorMessage,
          codec: job.codec,
          targetSizeMB: job.targetSizeMB,
          screenshots: job.screenshots || [],
          parentId: job.parentId,
          children: []
        }));
        setJobs((prev) => {
          const localUploading = prev.filter(
            (j) => j.id.startsWith('local-upload-') || j.id.startsWith('local-quick-')
          );
          // Mantém cards locais de upload ativo; evita sumir no poll de 2s
          const remoteIds = new Set(convertedJobs.map((j: VideoJob) => j.id));
          const stillLocal = localUploading.filter((j) => !remoteIds.has(j.id));
          return [...stillLocal, ...convertedJobs];
        });
      }
    } catch (error) {
      console.error('Erro ao carregar jobs:', error);
      showNotification('Erro ao conectar com o servidor', 'error');
    }
  }, []);

  useEffect(() => {
    loadJobs(filterType, customDate);
    // Atualizar jobs a cada 2 segundos
    const interval = setInterval(() => loadJobs(filterType, customDate), 2000);
    return () => clearInterval(interval);
  }, [filterType, customDate, loadJobs]);

  // Show notification
  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), type === 'error' ? 10000 : 4000);
  };

  // Handle new upload launcher items
  const handleUploadItemsReady = async (items: UploadItem[]) => {
    setIsAdvancedUploading(true);
    setAdvancedUploadProgress({});

    // Cards locais "enviando" até o job existir no backend
    const tempJobs: VideoJob[] = items.map((item) => ({
      id: `local-upload-${item.id}`,
      filename: item.customName,
      originalSize: item.file.size,
      status: 'uploading' as const,
      progress: 0,
      createdAt: new Date().toISOString(),
      codec: item.codec,
      targetSizeMB: item.targetSizeMB,
    }));
    setJobs((prev) => [...tempJobs, ...prev]);

    for (const item of items) {
      try {
        showNotification(`Enviando ${item.customName}`, 'info');
        await uploadVideoFile(
          {
            file: item.file,
            customName: item.customName,
            codec: item.codec,
            sizeMode: item.targetSizeMB ? 'target' : 'auto',
            targetSizeMB: item.targetSizeMB,
            width: item.width,
            height: item.height,
            normalizeAudio: item.normalizeAudio,
          },
          ({ loaded, total }) => {
            const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
            setAdvancedUploadProgress((prev) => ({
              ...prev,
              [item.id]: { loaded, total },
            }));
            setJobs((prev) =>
              prev.map((j) =>
                j.id === `local-upload-${item.id}`
                  ? { ...j, progress: pct, uploadLoaded: loaded, uploadTotal: total }
                  : j
              )
            );
          }
        );
        setJobs((prev) => prev.filter((j) => j.id !== `local-upload-${item.id}`));
        showNotification(`Upload de ${item.customName} iniciado!`, 'success');
        setTimeout(() => loadJobs(filterType, customDate), 1000);
      } catch (error) {
        console.error('Upload error:', error);
        const msg = error instanceof Error ? error.message : 'erro desconhecido';
        setJobs((prev) =>
          prev.map((j) =>
            j.id === `local-upload-${item.id}`
              ? { ...j, status: 'failed' as const, error: msg }
              : j
          )
        );
        showNotification(`Erro no upload de ${item.customName}: ${msg}`, 'error');
      }
    }

    setIsAdvancedUploading(false);
    setAdvancedUploadProgress({});
    setShowUploadLauncher(false);
  };

  // Handle quick config confirmation
  const handleQuickConfigConfirm = async (config: QuickUploadConfig) => {
    const files = quickConfigModal.files;
    setIsQuickUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const customName = file.name.replace(/\.[^/.]+$/, '');
      const tempId = `local-quick-${i}-${Date.now()}`;

      setJobs((prev) => [
        {
          id: tempId,
          filename: customName,
          originalSize: file.size,
          status: 'uploading' as const,
          progress: 0,
          createdAt: new Date().toISOString(),
          codec: config.codec,
          targetSizeMB: config.targetSizeMB,
        },
        ...prev,
      ]);

      try {
        showNotification(`Enviando ${file.name}`, 'info');
        setQuickUploadProgress({
          currentIndex: i + 1,
          totalFiles: files.length,
          loaded: 0,
          total: file.size,
          fileName: file.name,
        });

        await uploadVideoFile(
          {
            file,
            customName,
            codec: config.codec,
            sizeMode: config.targetSizeMB ? 'target' : 'auto',
            targetSizeMB: config.targetSizeMB,
            width: config.width,
            height: config.height,
            normalizeAudio: config.normalizeAudio,
          },
          ({ loaded, total }) => {
            const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
            setQuickUploadProgress({
              currentIndex: i + 1,
              totalFiles: files.length,
              loaded,
              total,
              fileName: file.name,
            });
            setJobs((prev) =>
              prev.map((j) =>
                j.id === tempId
                  ? { ...j, progress: pct, uploadLoaded: loaded, uploadTotal: total }
                  : j
              )
            );
          }
        );
        setJobs((prev) => prev.filter((j) => j.id !== tempId));
        showNotification(`Upload de ${file.name} iniciado!`, 'success');
        setTimeout(() => loadJobs(filterType, customDate), 1000);
      } catch (error) {
        console.error('Erro no upload:', error);
        const msg = error instanceof Error ? error.message : 'erro desconhecido';
        setJobs((prev) =>
          prev.map((j) =>
            j.id === tempId ? { ...j, status: 'failed' as const, error: msg } : j
          )
        );
        showNotification(`Erro ao enviar ${file.name}: ${msg}`, 'error');
      }
    }

    setIsQuickUploading(false);
    setQuickUploadProgress(null);
    setQuickConfigModal({ isOpen: false, files: [] });
  };

  // Real file upload to backend (now opens config modal first)
  const handleFileUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const videoFiles = fileArray.filter(file =>
      file.type.startsWith('video/') && file.size <= 2 * 1024 * 1024 * 1024 // 2GB limit
    );

    if (videoFiles.length === 0) {
      showNotification('Nenhum arquivo de vídeo válido selecionado', 'error');
      return;
    }

    // Open quick config modal instead of uploading directly
    setQuickConfigModal({ isOpen: true, files: videoFiles });
  };

  // Real delete job function
  const deleteJob = async (jobId: string) => {
    try {
      const response = await fetch(`/api/jobs/${jobId}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setDeleteModal({ isOpen: false, job: null });
        showNotification('Trabalho removido', 'success');
        // Recarregar jobs
        loadJobs(filterType, customDate);
      } else {
        throw new Error(result.error || 'Falha ao deletar');
      }
    } catch (error) {
      console.error('Erro ao deletar job:', error);
      showNotification('Erro ao remover trabalho', 'error');
    }
  };

  // Real download function
  const downloadFile = async (job: VideoJob) => {
    if (job.status !== 'completed') return;

    try {
      const response = await fetch('/api/download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jobId: job.id,
          filename: job.filename
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `compressed_${job.filename}`;
        link.click();
        window.URL.revokeObjectURL(url);
        showNotification(`Baixando ${job.filename}`, 'success');
      } else {
        throw new Error('Falha no download');
      }
    } catch (error) {
      console.error('Erro no download:', error);
      showNotification('Erro ao baixar arquivo', 'error');
    }
  };

  // Handle redo conversion
  const handleRedoConversion = async (config: RedoConfig) => {
    const job = redoConversionModal.job;
    if (!job) return;

    try {
      const requestBody = {
        parentJobId: job.id,
        codec: config.codec,
        sizeMode: config.targetSizeMB ? 'target' : 'auto',
        customName: `${job.filename.replace(/\.[^/.]+$/, '')}_${config.codec}`,
        ...(config.targetSizeMB && { targetSizeMB: config.targetSizeMB }),
        ...(config.width != null && { width: config.width }),
        ...(config.height != null && { height: config.height }),
        ...(config.normalizeAudio && { normalizeAudio: true }),
      };

      console.log('🔄 Sending redo conversion request:', requestBody);

      const response = await fetch('/api/upload/redo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        showNotification(`Reconversão de ${job.filename} iniciada!`, 'success');
        setTimeout(() => loadJobs(filterType, customDate), 1000);
      } else {
        const error = await response.json();
        showNotification(`Erro na reconversão: ${error.error}`, 'error');
      }
    } catch (error) {
      console.error('Redo conversion error:', error);
      showNotification('Erro na reconversão', 'error');
    }
  };

  // Open comparison modal
  const openComparisonModal = (jobId: string, timestamp?: number) => {
    setComparisonModal({
      isOpen: true,
      jobId,
      selectedTimestamp: timestamp
    });
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  // Delete job with confirmation
  const confirmDelete = (job: VideoJob) => {
    setDeleteModal({ isOpen: true, job });
  };




  // Handle file input
  const handleFileInput = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'video/*';
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        handleFileUpload(files);
      }
    };
    input.click();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 BYTES';
    const k = 1024;
    const sizes = ['BYTES', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getCompressionRatio = (original: number, compressed: number): string => {
    return Math.round((1 - compressed / original) * 100).toString();
  };

  const formatTimeRemaining = (seconds: number): string => {
    if (seconds <= 0) return "0s";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  // Função para converter UTC para horário de São Paulo (GMT-3)
  const formatDateBrasilia = (dateString: string, options: Intl.DateTimeFormatOptions = {}) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      ...options
    });
  };

  // Create hierarchical job structure (parent-children groups)
  const filteredJobs = React.useMemo(() => {
    // Jobs are already filtered by the API based on filterType and customDate
    const allJobs = jobs;

    // Group jobs by parent-child relationships
    const jobGroups = new Map<string, VideoJob[]>();

    // Identify parent jobs (jobs without parentId)
    const parentJobs = allJobs.filter(job => !job.parentId);

    // Create groups for each parent job
    parentJobs.forEach(parent => {
      jobGroups.set(parent.id, [parent]);
    });

    // Add children to their respective parent groups
    allJobs.forEach(job => {
      if (job.parentId && jobGroups.has(job.parentId)) {
        jobGroups.get(job.parentId)!.push(job);
      }
    });

    // Convert to flat array for rendering, preserving hierarchy
    const result: VideoJob[] = [];

    // Sort parent jobs by creation date (newest first)
    const sortedParents = parentJobs.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    sortedParents.forEach(parent => {
      const group = jobGroups.get(parent.id)!;
      // Add parent first
      result.push({ ...parent, isParent: true, hasChildren: group.length > 1 });

      // Add children (sorted by creation date, newest first)
      const children = group.slice(1).sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      children.forEach(child => {
        result.push({ ...child, isChild: true, parentJob: parent });
      });
    });

    return result;
  }, [jobs]);

  return (
    <div id="app-root" className="min-h-screen bg-retro-bg text-retro-text font-sans">
      {/* Header */}
      <header
        className="retro-card border-b-3 border-retro-line-soft rounded-none"
        role="banner"
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Logo and Title */}
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 bg-retro-primary border-3 border-retro-line rounded-retro flex items-center justify-center shadow-retro-1"
                aria-hidden="true"
              >
                <Zap className="w-6 h-6 text-black" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-pixel text-retro-primary text-retro-glow">
                  COMPRIMIR.EXE
                </h1>
                <p className="text-retro-muted text-xs sm:text-sm">
                  feito por VictorSamuel v1.1
                </p>
              </div>
            </div>

            {/* Header Actions */}
            <nav className="flex items-center gap-3" aria-label="Navegação de configurações">
              {/* User info */}
              <div className="text-right hidden sm:block">
                <p className="text-xs text-retro-muted">Conectado como:</p>
                <p className="text-sm text-retro-text font-medium">{user?.email}</p>
              </div>

              <RetroButton
                variant="ghost"
                size="sm"
                onClick={logout}
                aria-label="Fazer logout"
              >
                <LogOut className="w-4 h-4" aria-hidden="true" />
                SAIR
              </RetroButton>
            </nav>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Upload Zone */}
        <RetroCard hover className="p-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-retro-surface-2 border-3 border-retro-line-soft rounded-retro flex items-center justify-center">
              <Upload className="w-8 h-8 text-retro-primary" />
            </div>
            <div>
              <h2 className="text-xl font-pixel text-retro-primary mb-2">
                ZONA DE UPLOAD
              </h2>
              <p className="text-retro-muted text-sm">
                Drop = upload rápido · Máx 2GB · MP4, AVI, MOV, WEBM
              </p>
            </div>

            {/* Drag and Drop Zone — atalho do upload rápido */}
            <div
              className={`retro-dropzone group cursor-pointer ${isDragOver ? 'drag-over' : ''}`}
              role="button"
              tabIndex={0}
              aria-label="Solte vídeos para upload rápido ou clique para selecionar"
              onClick={handleFileInput}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleFileInput();
                }
              }}
            >
              <div className="space-y-3">
                <Film
                  className="w-12 h-12 mx-auto text-retro-muted group-hover:text-retro-primary transition-colors duration-retro-med"
                  aria-hidden="true"
                />
                <div>
                  <p className="font-medium text-retro-text">
                    SOLTE PARA UPLOAD RÁPIDO
                  </p>
                  <p className="text-sm text-retro-muted">
                    ou clique para selecionar
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <RetroButton size="lg" variant="primary" onClick={handleFileInput}>
                <Upload className="w-5 h-5" />
                UPLOAD RÁPIDO
              </RetroButton>
              <RetroButton size="lg" variant="secondary" onClick={() => setShowUploadLauncher(true)}>
                <Plus className="w-5 h-5" />
                UPLOAD AVANÇADO
              </RetroButton>
            </div>
            <p className="text-xs text-retro-muted">
              Rápido: codec + tamanho global · Avançado: por arquivo, resolução e volume
            </p>
          </div>
        </RetroCard>

        {/* Filters and Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Filter Controls */}
          <RetroCard className="p-4 lg:col-span-2">
            <div className="space-y-4">
              {/* Period Filter */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-retro-accent" />
                  <span className="font-medium text-sm" style={{color: '#a5add6'}}>PERÍODO:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <RetroButton
                    variant={filterType === 'all' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setFilterType('all');
                      setCustomDate('');
                    }}
                  >
                    TODOS
                  </RetroButton>
                  <RetroButton
                    variant={filterType === 'today' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setFilterType('today');
                      setCustomDate('');
                    }}
                  >
                    HOJE
                  </RetroButton>
                  <RetroButton
                    variant={filterType === 'week' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setFilterType('week');
                      setCustomDate('');
                    }}
                  >
                    SEMANA
                  </RetroButton>
                  <RetroButton
                    variant={filterType === 'month' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setFilterType('month');
                      setCustomDate('');
                    }}
                  >
                    MÊS
                  </RetroButton>
                </div>
              </div>
            </div>
          </RetroCard>

          {/* Quick Stats */}
          <RetroCard className="p-4">
            <div className="text-center">
              <div className="text-2xl font-pixel text-retro-primary">
                {jobs.filter(j => j.status === 'completed').length}
              </div>
              <div className="text-sm text-retro-muted">
                ARQUIVOS PROCESSADOS
              </div>
            </div>
          </RetroCard>
        </div>

        {/* Jobs Queue */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Square className="w-5 h-5 text-retro-accent" />
            <h3 className="text-lg font-pixel text-retro-text">
              FILA DE PROCESSAMENTO [{filteredJobs.length}]
            </h3>
          </div>

          {filteredJobs.length === 0 ? (
            <RetroCard className="p-8 text-center">
              <div className="text-retro-muted">
                <Film className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">NENHUM ARQUIVO NA FILA</p>
                <p className="text-sm">Envie alguns vídeos para começar</p>
              </div>
            </RetroCard>
          ) : (
            <div className="grid gap-4">
              {filteredJobs.map((job) => (
                <RetroCard
                  key={job.id}
                  hover
                  className={`p-4 ${job.isChild ? 'ml-8 border-l-4 border-retro-primary bg-retro-surface bg-opacity-50' : ''}`}
                >
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-center">
                      {/* File Info */}
                      <div className="lg:col-span-2 space-y-2">
                        <div className="flex items-center gap-3">
                          <StatusBadge status={job.status} />
                          {job.isChild && (
                            <span className="text-retro-primary text-sm">↳</span>
                          )}
                          <h4 className="font-medium text-retro-text truncate">
                            {job.filename}
                            {job.isChild && (
                              <span className="text-black text-xs ml-2 px-2 py-1 bg-retro-warn border border-retro-line rounded font-medium">
                                VERSÃO {job.codec?.toUpperCase()}
                              </span>
                            )}
                            {job.hasChildren && (
                              <span className="text-black text-xs ml-2 px-2 py-1 bg-retro-ok border border-retro-line rounded font-medium">
                                ORIGINAL
                              </span>
                            )}
                          </h4>
                        </div>
                        <div className="text-sm text-retro-muted">
                          {formatDateBrasilia(job.createdAt, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        {job.expiresAt && (
                          <div className="text-xs text-retro-warn">
                            🗑️ Exclui em {formatDateBrasilia(job.expiresAt, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                        )}
                      </div>

                      {/* Progress/Size Info */}
                      <div className="space-y-2">
                        {(job.status === 'uploading' || job.status === 'processing') && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-retro-muted">
                                {job.status === 'uploading' ? 'Upload' : 'Processando'}
                                {job.status === 'uploading' && job.uploadLoaded != null && job.uploadTotal != null && (
                                  <span className="text-retro-accent ml-2 font-mono text-xs">
                                    {formatUploadBytes(job.uploadLoaded)} / {formatUploadBytes(job.uploadTotal)}
                                  </span>
                                )}
                                {job.estimatedTimeRemaining && job.estimatedTimeRemaining > 0 && (
                                  <span className="text-retro-warn ml-2">
                                    {formatTimeRemaining(job.estimatedTimeRemaining)} restante
                                  </span>
                                )}
                              </span>
                              <span className="text-retro-primary font-medium">{Math.round(job.progress)}%</span>
                            </div>
                            <NeonProgress
                              value={job.progress || 0}
                              variant={job.status === 'uploading' ? 'upload' : 'processing'}
                              label={`Progresso de ${job.status === 'uploading' ? 'upload' : 'processamento'} para ${job.filename}: ${Math.round(job.progress)}%`}
                              showPercentage={false}
                              animated={true}
                            />
                          </>
                        )}

                        <div className="text-sm space-y-1">
                          <div className="text-retro-muted">
                            Tamanho: <span className="text-retro-text font-medium">{formatFileSize(job.originalSize)}</span>
                          </div>
                          {job.compressedSize && (
                            <div className="text-retro-ok">
                              → {formatFileSize(job.compressedSize)}
                              <span className="text-retro-warn ml-1">
                                (-{getCompressionRatio(job.originalSize, job.compressedSize)}%)
                              </span>
                            </div>
                          )}
                          {job.codec && (
                            <div className="text-retro-muted">
                              Codec: <span className="text-retro-accent font-medium">{job.codec.toUpperCase()}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex justify-end gap-2">
                        {job.status === 'completed' && (
                          <>
                            <Tooltip content="Baixar arquivo comprimido">
                              <RetroButton
                                variant="secondary"
                                size="sm"
                                onClick={() => downloadFile(job)}
                                aria-label={`Baixar ${job.filename}`}
                              >
                                <Download className="w-4 h-4" />
                              </RetroButton>
                            </Tooltip>

                            <Tooltip content="Refazer conversão com outras configurações">
                              <RetroButton
                                variant="secondary"
                                size="sm"
                                onClick={() => setRedoConversionModal({ isOpen: true, job })}
                                aria-label={`Refazer conversão de ${job.filename}`}
                              >
                                <RefreshCw className="w-4 h-4" />
                              </RetroButton>
                            </Tooltip>
                          </>
                        )}

                        <Tooltip content="Excluir arquivo">
                          <RetroButton
                            variant="ghost"
                            size="sm"
                            onClick={() => confirmDelete(job)}
                            aria-label={`Excluir ${job.filename}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </RetroButton>
                        </Tooltip>
                      </div>
                    </div>

                    {/* Screenshots Section */}
                    {job.screenshots && job.screenshots.length > 0 && (
                      <div className="border-t-2 border-retro-line-soft pt-3">
                        <div className="flex items-center gap-2 mb-3">
                          <ImageIcon className="w-4 h-4 text-retro-accent" />
                          <span className="text-retro-pixel text-xs text-retro-accent">SCREENSHOTS</span>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {Array.from(new Set(job.screenshots.map(s => s.timestamp))).sort().map(timestamp => {
                            const originalShot = job.screenshots!.find(s => s.type === 'original' && s.timestamp === timestamp);
                            const processedShot = job.screenshots!.find(s => s.type === 'processed' && s.timestamp === timestamp);

                            const formatTimestamp = (ts: number) => {
                              const minutes = Math.floor(ts / 60);
                              const seconds = Math.floor(ts % 60);
                              return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                            };

                            return (
                              <div
                                key={timestamp}
                                className="relative group cursor-pointer"
                                onClick={() => openComparisonModal(job.id, timestamp)}
                              >
                                <div className="w-20 h-14 border-2 border-retro-line rounded bg-retro-surface overflow-hidden hover:border-retro-accent transition-colors">
                                  {processedShot ? (
                                    <img
                                      src={`/api/screenshots/${processedShot.filePath.split('/').pop()}`}
                                      alt={`Screenshot em ${formatTimestamp(timestamp)}`}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : originalShot ? (
                                    <img
                                      src={`/api/screenshots/${originalShot.filePath.split('/').pop()}`}
                                      alt={`Screenshot em ${formatTimestamp(timestamp)}`}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-retro-muted">
                                      <ImageIcon className="w-6 h-6" />
                                    </div>
                                  )}
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-retro-text text-xs px-1 py-0.5 text-center">
                                  {formatTimestamp(timestamp)}
                                </div>
                                <div className="absolute inset-0 bg-retro-accent bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded flex items-center justify-center">
                                  <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                                    👁️ Ver
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </RetroCard>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && deleteModal.job && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75">
          <div
            className="w-full max-w-md"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-title"
            aria-describedby="delete-description"
          >
            <RetroCard className="p-6 border-retro-error">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-retro-error border-2 border-retro-line rounded-retro flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 id="delete-title" className="text-lg font-pixel text-retro-text">
                      EXCLUIR ARQUIVO
                    </h3>
                    <p className="text-sm text-retro-muted">
                      Esta ação não pode ser desfeita
                    </p>
                  </div>
                </div>

                <div id="delete-description" className="space-y-2">
                  <p className="text-retro-text">
                    Tem certeza que deseja excluir:
                  </p>
                  <p className="font-medium text-retro-primary bg-retro-surface-2 p-2 rounded border-2 border-retro-line-soft">
                    {deleteModal.job.filename}
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <RetroButton
                    variant="secondary"
                    className="flex-1"
                    onClick={() => setDeleteModal({ isOpen: false, job: null })}
                    autoFocus
                  >
                    CANCELAR
                  </RetroButton>
                  <RetroButton
                    variant="primary"
                    className="flex-1 bg-retro-error border-retro-error hover:bg-retro-error"
                    onClick={() => deleteJob(deleteModal.job!.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                    EXCLUIR
                  </RetroButton>
                </div>
              </div>
            </RetroCard>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {notification && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-in">
          <RetroCard className={`p-4 ${
            notification.type === 'success' ? 'border-retro-ok' :
            notification.type === 'error' ? 'border-retro-error' : 'border-retro-warn'
          }`}>
            <div className="flex items-center gap-2">
              {notification.type === 'success' && <CheckCircle className="w-5 h-5 text-retro-ok" />}
              {notification.type === 'error' && <XCircle className="w-5 h-5 text-retro-error" />}
              {notification.type === 'info' && <Clock className="w-5 h-5 text-retro-warn" />}
              <span className="text-sm font-medium">{notification.message}</span>
            </div>
          </RetroCard>
        </div>
      )}

      {/* Upload Launcher Modal */}
      {showUploadLauncher && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
          <div className="retro-card max-w-4xl w-full max-h-[90vh] overflow-y-auto border-4 border-retro-primary shadow-retro-3">
            <div className="p-4 border-b-2 border-retro-line bg-retro-accent">
              <div className="flex justify-between items-center">
                <h2 className="text-retro-pixel text-xl text-black">
                  UPLOAD AVANÇADO
                </h2>
                <button
                  onClick={() => {
                    if (!isAdvancedUploading) setShowUploadLauncher(false);
                  }}
                  className="retro-button retro-button-danger px-3 py-2 text-sm"
                  disabled={isAdvancedUploading}
                  type="button"
                >
                  ✕ FECHAR
                </button>
              </div>
            </div>

            <div className="p-4">
              <UploadLauncher
                onItemsReady={handleUploadItemsReady}
                uploadProgress={advancedUploadProgress}
                isUploading={isAdvancedUploading}
              />
            </div>
          </div>
        </div>
      )}

      {/* Quick Config Modal for Simple Uploads */}
      <QuickConfigModal
        files={quickConfigModal.files}
        isOpen={quickConfigModal.isOpen}
        onConfirm={handleQuickConfigConfirm}
        onCancel={() => {
          if (!isQuickUploading) setQuickConfigModal({ isOpen: false, files: [] });
        }}
        uploadProgress={quickUploadProgress}
        isUploading={isQuickUploading}
      />

      {/* Comparison Modal */}
      <ComparisonModal
        isOpen={comparisonModal.isOpen}
        onClose={() => setComparisonModal({ isOpen: false, jobId: '', selectedTimestamp: undefined })}
        screenshots={comparisonModal.jobId ? jobs.find(j => j.id === comparisonModal.jobId)?.screenshots || [] : []}
        selectedTimestamp={comparisonModal.selectedTimestamp}
        jobId={comparisonModal.jobId}
      />

      {/* Redo Conversion Modal */}
      <RedoConversionModal
        isOpen={redoConversionModal.isOpen}
        onClose={() => setRedoConversionModal({ isOpen: false, job: null })}
        onConfirm={handleRedoConversion}
        job={redoConversionModal.job}
      />
    </div>
  );
}

export default App;